import type { FC } from 'react';
import { useCallback } from 'react';

import type { DrawerProps } from '@mui/material';
import { Drawer, Typography, drawerClasses } from '@mui/material';

import { EventHistory } from 'pages/projects/projectPage/selectedProject/projectHistory/EventHistory';
import type {
  EventHistoryInfo,
  NodeInfo
} from 'pages/projects/projectPage/selectedProject/projectHistory/EventHistory.types';
import { ProjectHistoryPanelHeader } from 'pages/projects/projectPage/selectedProject/projectHistory/ProjectHistoryPanelHeader';
import { useGridActions } from 'state/grids/gridState';
import type { ProjectHistory } from 'state/history/historyState';
import { HISTORY_STORE } from 'state/history/historyStore';
import { useSelectedProjectTemplateNodeDisplayNameAndIcon } from 'state/selectedProject/selectedProjectHooks';

export interface ProjectHistoryPanelProps extends DrawerProps {
  currentProjectHistoryState: ProjectHistory | undefined;
}

export const ProjectHistoryPanel: FC<ProjectHistoryPanelProps> = ({ currentProjectHistoryState, ...other }) => {
  const gridActions = useGridActions();
  const selectedTemplateNodeId = HISTORY_STORE((state) => state.selectedTemplateNodeId);
  const selectedNodeProperty = HISTORY_STORE((state) => state.selectedNodeProperty);
  const selectedTargetPropertyName = HISTORY_STORE((state) => state.selectedTargetPropertyName);
  const selectedTargetPropertyType = HISTORY_STORE((state) => state.selectedTargetPropertyType);
  const eventsHistoryData = HISTORY_STORE((state) => state.eventsHistory);

  const projectId = selectedNodeProperty?.split('_')[0];
  const nodeId = selectedNodeProperty?.split('_')[1];
  const propertyId = selectedNodeProperty?.split('_')[2];

  const eventsHistory = eventsHistoryData[selectedNodeProperty!] || [];

  const isFetching = HISTORY_STORE((state) => state.isFetching);

  const { icon, name } = useSelectedProjectTemplateNodeDisplayNameAndIcon(selectedTemplateNodeId);

  const targetNode: NodeInfo = {
    id: nodeId || '',
    icon: icon,
    fieldName: name,
    fieldPropertyDisplayName: selectedTargetPropertyName,
    fieldPropertyType: selectedTargetPropertyType
  };

  const groupedEventsHistoryByDate = (eventHistoryInfos: EventHistoryInfo[]) => {
    type EventHistoryGroupedByDate = {
      timeDate: string;
      eventHistory: EventHistoryInfo[];
    };

    //get all dates without time
    const eventsHistoryDay = eventHistoryInfos.map((eventHistoryInfo) => {
      return eventHistoryInfo.timeStamp.split('T')[0];
    });

    // return string today or yesterday if eventsHistoryDay is today or yesterday
    const getTodayOrYesterday = (date: string) => {
      const today = new Date().toDateString();
      const yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toDateString();
      const dateToCheck = new Date(date).toDateString();
      if (dateToCheck === today) {
        return 'Today';
      } else if (dateToCheck === yesterday) {
        return 'Yesterday';
      } else {
        return dateToCheck;
      }
    };

    const groupedEventsByDate = eventsHistoryDay.reduce<EventHistoryGroupedByDate>((acc: any, date) => {
      acc[getTodayOrYesterday(date)] = eventHistoryInfos.filter(
        (eventHistoryInfo) => eventHistoryInfo.timeStamp.split('T')[0] === date
      );
      return acc;
    }, {} as EventHistoryGroupedByDate);

    return groupedEventsByDate;
  };

  const eventsHistoryEntries = Object.entries(groupedEventsHistoryByDate(eventsHistory));

  const onRefreshProjectHistory = useCallback(() => {
    projectId && nodeId && propertyId && gridActions.getNodePropertyProjectHistory(projectId, nodeId, propertyId);
  }, [projectId, nodeId, propertyId, gridActions]);

  return (
    <>
      {currentProjectHistoryState !== undefined && (
        <Drawer
          anchor='right'
          variant='persistent'
          sx={(theme) => ({
            flexShrink: 0,
            width: currentProjectHistoryState.open ? '400px' : 0,

            [`& .${drawerClasses.paper}`]: {
              background: theme.palette.grey[100],
              borderLeft: currentProjectHistoryState.open ? `1 px solid ${theme.palette.grey[300]}` : '',
              padding: '18px 24px',
              width: '400px'
            }
          })}
          open={currentProjectHistoryState.open}
          hideBackdrop
          {...other}
        >
          <ProjectHistoryPanelHeader
            onClose={() => projectId && HISTORY_STORE.getState().close(projectId)}
            onRefreshProjectHistory={onRefreshProjectHistory}
            isFetching={isFetching}
          />

          {eventsHistoryEntries.length > 0 ? (
            eventsHistoryEntries.map(([key, entry]) => {
              return <EventHistory key={key} date={key} eventsHistoryInfo={entry as any} targetNode={targetNode} />;
            })
          ) : (
            <Typography color='text.disabled' my='16px'>
              No history available
            </Typography>
          )}
        </Drawer>
      )}
    </>
  );
};
